/**
 * Free-shipping spend tracker — keeps every instance in step with the basket.
 *
 * Why it re-reads /cart.js rather than using the event payload: Shopify's storefront standard
 * events module normalises the cart to { totalQuantity, cost, lines }, and none of those lines
 * carry `requires_shipping`. Gift cards have to be excluded from the count (Liam, 6 Aug), so the
 * flag is needed — and /cart.js is the only place that has it. It is roughly a kilobyte of JSON
 * and is only ever fetched when the basket actually changes, never on page load: the Liquid has
 * already rendered the correct state by then.
 *
 * The event name comes from https://cdn.shopify.com/storefront/standard-events.js, where
 * StandardEvents.cartLinesUpdate resolves to 'shopify:cart:lines-update'. It is written out
 * rather than imported so this file stays a plain deferred script with no module graph.
 */
(function () {
  'use strict';

  var CART_UPDATE_EVENT = 'shopify:cart:lines-update';

  /** Whether the threshold celebration has already run on this page view. */
  var celebrated = false;

  /**
   * Formats pence using the shop's own money format, so this does not assume sterling even
   * though the tracker currently only renders for GB.
   */
  function formatMoney(cents, format) {
    function grouped(decimals, thousands, decimalMark) {
      var parts = (cents / 100).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimalMark || '');
    }

    return (format || '£{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, function (_match, name) {
      switch (name) {
        case 'amount_no_decimals':
          return grouped(0, ',');
        case 'amount_with_comma_separator':
          return grouped(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return grouped(0, '.');
        case 'amount_with_apostrophe_separator':
          return grouped(2, "'", '.');
        default:
          return grouped(2, ',', '.');
      }
    });
  }

  /** Only lines that actually get posted count towards the threshold. */
  function shippableTotal(cart) {
    var total = cart.total_price;
    (cart.items || []).forEach(function (item) {
      if (!item.requires_shipping) total -= item.final_line_price;
    });
    return Math.max(total, 0);
  }

  /**
   * Writes the message without innerHTML: the copy comes from a theme setting, and building it
   * out of text nodes means no escaping step can be forgotten later.
   */
  function writeMessage(target, template, amount) {
    var pieces = String(template).split('[amount]');
    target.textContent = '';
    target.appendChild(document.createTextNode(pieces[0]));

    if (pieces.length > 1) {
      var strong = document.createElement('b');
      strong.textContent = amount;
      target.appendChild(strong);
      target.appendChild(document.createTextNode(pieces.slice(1).join('[amount]')));
    }
  }

  function render(el, cart) {
    var threshold = parseInt(el.dataset.threshold, 10);
    if (!threshold) return;

    var total = shippableTotal(cart);
    var remaining = Math.max(threshold - total, 0);
    var state = total <= 0 ? 'empty' : remaining > 0 ? 'progress' : 'done';

    var text = el.querySelector('[data-mts-ship-text]');
    var fill = el.querySelector('[data-mts-ship-fill]');
    if (!text || !fill) return;

    // Read before writing: the attribute still holds the state this tracker was last rendered in,
    // which is what makes this a transition rather than a condition. Loading a page with a basket
    // already over the threshold must not fire anything — nothing has just been achieved.
    var previous = el.dataset.state;

    el.dataset.state = state;
    fill.style.width = Math.min((total / threshold) * 100, 100) + '%';

    if (state === 'progress') {
      writeMessage(text, el.dataset.textProgress, formatMoney(remaining, el.dataset.moneyFormat));
    } else {
      writeMessage(text, state === 'done' ? el.dataset.textDone : el.dataset.textEmpty, '');
    }

    // Once per page view. There are up to three trackers on a page and they all cross together, so
    // the guard inside mtsCelebrate stops three simultaneous bursts; this stops a fourth when
    // someone nudges the quantity down and back up again.
    if (state === 'done' && previous && previous !== 'done' && !celebrated) {
      celebrated = true;
      if (typeof window.mtsCelebrate === 'function') window.mtsCelebrate();
    }
  }

  /**
   * The cart event is dispatched *optimistically* — before the mutation has landed. Horizon's own
   * components handle this by waiting on `event.promise`, which resolves once the cart is actually
   * updated (see `assets/cart-icon.js`). Reading /cart.js the moment the event arrives returns the
   * basket as it was a moment ago, which is exactly why a quantity change left the tracker one
   * step behind while an add-to-cart appeared to work.
   *
   * The promise's own payload is not enough on its own — it has no `requires_shipping` — so this
   * waits for it and then reads /cart.js.
   */
  function refresh(event) {
    var settled = event && event.promise ? Promise.resolve(event.promise).catch(function () {}) : Promise.resolve();

    settled.then(reload);
  }

  function reload() {
    var bars = document.querySelectorAll('[data-mts-ship]');
    if (!bars.length) return;

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('cart.js responded ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        var paint = function () {
          document.querySelectorAll('[data-mts-ship]').forEach(function (el) {
            render(el, cart);
          });
        };

        paint();
        // The cart drawer re-renders itself after some updates, which swaps the tracker inside it
        // for markup from the previous state. Painting again on the next frame puts it back in
        // step, and re-uses the cart we already have rather than fetching twice.
        requestAnimationFrame(paint);
      })
      .catch(function (error) {
        // Leaving the server-rendered state in place is the safe failure: it is stale rather
        // than wrong, and it never shows a threshold the customer has not actually reached.
        console.warn('[mts-shipping-bar]', error);
      });
  }

  // Capture phase: a quantity change dispatches this event on <cart-items-component> rather than
  // on document (verified in the browser). Capture runs on the way down from window to the target,
  // so it fires wherever in the tree the event originates and whether or not it bubbles.
  document.addEventListener(CART_UPDATE_EVENT, refresh, true);

  // Coming back via the back/forward cache can restore a page whose basket has since changed.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refresh();
  });
})();
